import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useCRUD from "../../hooks/useCRUD";
import Participants from "./Participants";
import Drivers from "./Drivers";
import useFileUpload from "../../hooks/useFileUpload";
import UploadModal from "./UploadModal";
import BulkSmsModal from "./BulkSmsModal";
import PendingJoinRequests from "./PendingJoinRequests";
import PublicJoinLinkCard from "./PublicJoinLinkCard";
import Spinner from "../Spinner/Spinner";
import WithFeatureRestriction from "../WithFeatureRestriction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCarSide,
  faPaperPlane,
  faUpload,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

const formatUploadErrors = (result) => {
  if (!result) return "Upload failed. Please try again.";
  if (!result.errors)
    return result.message || "Upload failed. Please try again.";

  if (Array.isArray(result.errors)) {
    return result.errors
      .map((error) => {
        if (typeof error === "string") return error;
        if (error.row && Array.isArray(error.errors)) {
          return `Row ${error.row}: ${error.errors.join(" ")}`;
        }
        if (Array.isArray(error)) return error.join(" ");
        return error.message || JSON.stringify(error);
      })
      .join("\n");
  }

  return Object.entries(result.errors)
    .map(([field, messages]) => `${field}: ${[].concat(messages).join(" ")}`)
    .join("\n");
};

const ManageGroup = () => {
  const { id } = useParams();

  const { data: groupDetails, fetchItems: getGroupDetails } = useCRUD(
    `/api/participant-groups/${id}`,
  );

  const [drivers, setDrivers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBulkSmsModalOpen, setIsBulkSmsModalOpen] = useState(false);

  const { uploadFile } = useFileUpload("/api/participants/upload");

  useEffect(() => {
    getGroupDetails();
  }, []);
  useEffect(() => {
    if (groupDetails) {
      const filteredParticipants =
        groupDetails.participants?.filter((member) => !member.isDriver) || [];
      setParticipants(filteredParticipants);

      const filteredDrivers =
        groupDetails.participants?.filter((member) => member.isDriver) || [];
      setDrivers(filteredDrivers);
    }
  }, [groupDetails]); // Watch for changes in groupDetails

  const handleUpload = async (file) => {
    const participantGroupId = id;
    let result;

    try {
      result = await uploadFile(file, { participantGroupId });
      // Assuming result contains the response body
      // Check if the upload was successful, based on your API's success response structure
      if (result?.ok && !result.errors) {
        console.log("File uploaded successfully:", result);
        getGroupDetails();
        alert("File uploaded successfully.");
        return { ok: true };
      } else {
        return {
          ok: false,
          message: formatUploadErrors(result),
        };
      }
    } catch (error) {
      // This catch block is for network errors or any issues in the try block
      console.error("Upload error:", error);
      // Assuming 'error' is an object that contains a message, adjust as needed
      return {
        ok: false,
        message: error.message || "An unknown upload error occurred.",
      };
    }
  };

  const handleSendBulkSms = async (payload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participant-groups/${id}/bulk-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          ok: false,
          message: result.message || "Bulk SMS failed. Please try again.",
        };
      }
      return { ok: true, ...result };
    } catch (error) {
      return {
        ok: false,
        message: error.message || "Bulk SMS failed. Please try again.",
      };
    }
  };

  if (!groupDetails) {
    return <Spinner />;
  }

  const isRSVPOnly = groupDetails.eventType === "RSVP Only";
  const totalMembers = groupDetails.participants?.length || 0;

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="rounded-xl bg-white p-5 shadow-lg">
          <Link
            to="/groups"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#004a7e] hover:text-[#003a64]"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to groups
          </Link>

          <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#004a7e]">
                Group management
              </p>
              <h1 className="mt-2 text-2xl font-black text-gray-900 sm:text-3xl">
                {groupDetails?.groupName}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage members, drivers, app invites, and pickup GPS for this
                group.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#004a7e] bg-white px-4 py-3 text-sm font-bold text-[#004a7e] shadow-sm hover:bg-[#eaf3fa] sm:w-auto"
                onClick={() => setIsBulkSmsModalOpen(true)}
              >
                <FontAwesomeIcon icon={faPaperPlane} /> Send Bulk SMS
              </button>
              <WithFeatureRestriction featureKey="upload_attendees_spreadsheet">
                {(isDisabled) => (
                  <button
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#004a7e] px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#003a64] disabled:opacity-40 sm:w-auto"
                    onClick={() => !isDisabled && setIsUploadModalOpen(true)}
                    disabled={isDisabled}
                  >
                    <FontAwesomeIcon icon={faUpload} /> Upload Spreadsheet
                  </button>
                )}
              </WithFeatureRestriction>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <GroupStat icon={faUsers} label="Total" value={totalMembers} />
            <GroupStat
              icon={faUsers}
              label="Members"
              value={participants.length}
            />
            {!isRSVPOnly && (
              <GroupStat
                icon={faCarSide}
                label="Drivers"
                value={drivers.length}
              />
            )}
          </div>
        </div>

        <PublicJoinLinkCard group={groupDetails} onChanged={getGroupDetails} />

        <PendingJoinRequests groupId={id} onChanged={getGroupDetails} />

        <div className="sticky top-0 z-20 -mx-4 border-y border-gray-200 bg-white/95 px-4 py-2 shadow-sm backdrop-blur sm:static sm:mx-0 sm:rounded-xl sm:border sm:shadow-sm">
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            <a
              href="#members"
              className="whitespace-nowrap rounded-lg bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-[#eaf3fa] hover:text-[#004a7e]"
            >
              Members
            </a>
            {!isRSVPOnly && (
              <a
                href="#drivers"
                className="whitespace-nowrap rounded-lg bg-gray-100 px-3 py-2 text-xs font-bold text-gray-700 hover:bg-[#eaf3fa] hover:text-[#004a7e]"
              >
                Drivers
              </a>
            )}
          </div>
        </div>

        {isUploadModalOpen && (
          <UploadModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            onUpload={handleUpload}
            isRSVPOnly={isRSVPOnly}
          />
        )}

        {isBulkSmsModalOpen && (
          <BulkSmsModal
            isOpen={isBulkSmsModalOpen}
            onClose={() => setIsBulkSmsModalOpen(false)}
            recipients={groupDetails.participants || []}
            onSend={handleSendBulkSms}
          />
        )}

        <section id="members" className="scroll-mt-24">
          <Participants
            participants={participants}
            refreshData={getGroupDetails}
            event={groupDetails}
            groupId={id}
            isRSVPOnly={isRSVPOnly}
          />
        </section>

        {!isRSVPOnly && (
          <section id="drivers" className="scroll-mt-24">
            <Drivers
              drivers={drivers}
              refreshData={getGroupDetails}
              event={groupDetails}
              groupId={id}
            />
          </section>
        )}
      </div>
    </div>
  );
};

const GroupStat = ({ icon, label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#eaf3fa] text-[#004a7e]">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
          {label}
        </p>
        <p className="text-2xl font-black text-[#004a7e]">{value}</p>
      </div>
    </div>
  </div>
);

export default ManageGroup;
